import React, { type FC, useContext, createContext, useState } from "react";
import { BarChart3Icon, Loader2Icon, ChevronDownIcon, ChevronUpIcon, PlayIcon, CheckCircle2Icon, XCircleIcon, Info } from "lucide-react";
import { Chart } from "../Chart";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { FullScreenTakeover } from "../FullScreenTakeover";
import { DataExplorer } from "../DrilldownChart";
import { useAnalysis } from "../../hooks/useAnalysis";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";

// Context for tool call handlers - exported so thread.tsx can provide the value
export type ToolCallHandlers = {
  onAnalysisGenerated?: (analysisId: string) => void;
  onPlanExecuted?: (result: unknown) => void;
  onSendMessage?: (message: string) => void;
  onResumeGraph?: (value: any) => void;
};

export const ToolCallHandlersContext = createContext<ToolCallHandlers | undefined>(undefined);

// Context for controlling autoRun behavior - historical threads should not autoRun
export const ChartAutoRunContext = createContext<boolean>(true);

// Placeholder component that shows analysis info before loading
const ChartPlaceholder: FC<{
  analysisId: string;
  onLoad: () => void;
}> = ({ analysisId, onLoad }) => {
  const ChartIcon = BarChart3Icon; // Default icon

  return (
    <div className="aui-chart-placeholder">
      <div className="aui-chart-placeholder-content">
        <ChartIcon className="aui-chart-placeholder-icon" />
        <div className="text-center mb-3">
          <h5 className="mb-1 text-foreground">Chart Analysis</h5>
          <p className="text-muted-foreground mb-0">Analysis ID: {analysisId}</p>
        </div>
        <Button
          variant="default"
          className="inline-flex items-center"
          onClick={onLoad}
        >
          <PlayIcon className="mr-2 h-4 w-4" />
          View Chart
        </Button>
      </div>
    </div>
  );
};

// Helper function to normalize comparator text
const normalizeComparator = (comparator: string): string => {
  const normalized = comparator.toUpperCase();
  if (normalized === 'EQUALS' || normalized === '=') {
    return '=';
  }
  if (normalized === 'NOTEQUALS' || normalized === '!=' || normalized === '<>') {
    return '!=';
  }
  if (normalized === 'GREATERTHAN' || normalized === '>') {
    return '>';
  }
  if (normalized === 'LESSTHAN' || normalized === '<') {
    return '<';
  }
  if (normalized === 'GREATERTHANOREQUAL' || normalized === '>=') {
    return '>=';
  }
  if (normalized === 'LESSTHANOREQUAL' || normalized === '<=') {
    return '<=';
  }
  if (normalized === 'IN') {
    return 'in';
  }
  if (normalized === 'NOTIN') {
    return 'not in';
  }
  return comparator;
};

const formatDateRange = (start: string, end: string): string => {
  const s = new Date(start + 'T00:00:00');
  const e = new Date(end + 'T00:00:00');
  const sameYear = s.getFullYear() === e.getFullYear();
  const fmt = (d: Date, includeYear: boolean) =>
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(includeYear ? { year: 'numeric' } : {}) });
  return `${fmt(s, !sameYear)} – ${fmt(e, true)}`;
};

// Helper function to format chart details from args
const formatChartDetails = (argsText: string): React.ReactNode => {
  try {
    const args = typeof argsText === 'string' ? JSON.parse(argsText) : argsText;

    const metrics = args?.metrics || [];
    const dimensions = args?.dimensions || [];
    const whereClauses = args?.where_clauses || [];
    const dateRangeStart = args?.date_range_start;
    const dateRangeEnd = args?.date_range_end;
    
    // Format measures
    let measuresElements: React.ReactNode[] = [];
    if (metrics.length === 0) {
      measuresElements = [<span key="no-measures">no measures</span>];
    } else if (metrics.length === 1) {
      measuresElements = [<strong key={metrics[0]}>{metrics[0]}</strong>];
    } else if (metrics.length === 2) {
      measuresElements = [
        <strong key={metrics[0]}>{metrics[0]}</strong>,
        ' and ',
        <strong key={metrics[1]}>{metrics[1]}</strong>
      ];
    } else {
      const last = metrics[metrics.length - 1];
      const rest = metrics.slice(0, -1);
      measuresElements = [
        ...rest.map((m: string, idx: number) => (
          <React.Fragment key={m}>
            <strong>{m}</strong>
            {idx < rest.length - 1 ? ', ' : ''}
          </React.Fragment>
        )),
        ', and ',
        <strong key={last}>{last}</strong>
      ];
    }
    
    // Format dimensions
    let dimensionsElements: React.ReactNode[] = [];
    if (dimensions.length === 0) {
      dimensionsElements = [<span key="no-dimensions">no dimensions</span>];
    } else if (dimensions.length === 1) {
      dimensionsElements = [<strong key={dimensions[0]}>{dimensions[0]}</strong>];
    } else if (dimensions.length === 2) {
      dimensionsElements = [
        <strong key={dimensions[0]}>{dimensions[0]}</strong>,
        ' and ',
        <strong key={dimensions[1]}>{dimensions[1]}</strong>
      ];
    } else {
      const last = dimensions[dimensions.length - 1];
      const rest = dimensions.slice(0, -1);
      dimensionsElements = [
        ...rest.map((d: string, idx: number) => (
          <React.Fragment key={d}>
            <strong>{d}</strong>
            {idx < rest.length - 1 ? ', ' : ''}
          </React.Fragment>
        )),
        ', and ',
        <strong key={last}>{last}</strong>
      ];
    }
    
    // Format filters
    let filtersElements: React.ReactNode[] = [];
    if (whereClauses.length > 0) {
      filtersElements = whereClauses.map((clause: any, idx: number) => {
        const dimension = clause.dimension || '';
        const comparator = normalizeComparator(clause.comparator || '=');
        const values = Array.isArray(clause.value) ? clause.value : [clause.value];
        
        return (
          <React.Fragment key={idx}>
            <strong>{dimension}</strong>
            {' '}
            <strong>{comparator}</strong>
            {' '}
            {values.length === 1 ? (
              <strong>"{values[0]}"</strong>
            ) : (
              <strong>
                [{values.map((v: string, i: number) => (
                  <React.Fragment key={i}>
                    "{v}"{i < values.length - 1 ? ', ' : ''}
                  </React.Fragment>
                ))}]
              </strong>
            )}
            {idx < whereClauses.length - 1 ? ', ' : ''}
          </React.Fragment>
        );
      });
    }
    
    return (
      <>
        Generating chart from semantic layer:{' '}
        {measuresElements}
        {' '}broken down by{' '}
        {dimensionsElements}
        {filtersElements.length > 0 && (
          <>
            , filtered where{' '}
            {filtersElements}
          </>
        )}
        {dateRangeStart && dateRangeEnd && (
          <>
            , for <strong>{formatDateRange(dateRangeStart, dateRangeEnd)}</strong>
          </>
        )}
      </>
    );
  } catch (e) {
    return 'Generating chart from semantic layer...';
  }
};

// Custom tool component for chart generation tools
const ChartToolCallComponent: FC<{
  toolName: string;
  argsText: string;
  result?: string;
  status?: { type: "running" | "complete" | "incomplete"; reason?: string };
}> = ({ argsText, result, status }) => {
  const handlers = useContext(ToolCallHandlersContext);
  const autoRunFromContext = useContext(ChartAutoRunContext);
  const isRunning = status?.type === "running" || (status === undefined && result === undefined);
  const isError = status?.type === "incomplete" && status.reason === "error";
  
  // Detect error from result if status doesn't indicate it
  let hasError = isError;
  let fullErrorMessage: string | null = null;
  
  // If status indicates an error, try to get the error message from status.reason or result
  if (isError) {
    fullErrorMessage = status?.reason || null;
  }
  
  // Also check result for error details
  if (result) {
    try {
      const parsed = typeof result === 'string' ? JSON.parse(result) : result;
      if (parsed && (parsed.error || parsed.exception)) {
        hasError = true;
        fullErrorMessage = parsed.error || parsed.exception || fullErrorMessage;
      }
    } catch (e) {
      // If result is a plain string that looks like an error
      if (typeof result === 'string' && (result.toLowerCase().includes('error') || result.toLowerCase().includes('exception'))) {
        hasError = true;
        fullErrorMessage = result || fullErrorMessage;
      }
    }
  }
  
  // Tool is complete only when status is explicitly 'complete' from backend
  const isComplete = status?.type === "complete" && !hasError;
  
  // Start collapsed for historical threads (when autoRun is false)
  const [isExpanded, setIsExpanded] = useState(autoRunFromContext);
  const [isLoaded, setIsLoaded] = useState(autoRunFromContext);
  const [showDataExplorer, setShowDataExplorer] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);

  // Parse the result to get analysis_id (only if not an error)
  let analysisId: string | null = null;
  if (result && !hasError) {
    try {
      const parsed = typeof result === 'string' ? JSON.parse(result) : result;
      analysisId = parsed.analysis_id;
    } catch (e) {
      // Ignore parse errors
    }
  }

  // Fetch analysis data to get the chart name
  const { data: analysis } = useAnalysis(analysisId, false);
  const chartName = analysis?.name || analysisId || 'Loading...';
  
  // Format the chart details message
  // Only show detailed message if we have valid args, otherwise show simple message
  let chartDetailsMessage: React.ReactNode;
  try {
    const args = typeof argsText === 'string' ? JSON.parse(argsText) : argsText;
    // Check if we have meaningful args (not just empty arrays/objects)
    const hasMetrics = args?.metrics && Array.isArray(args.metrics) && args.metrics.length > 0;
    const hasDimensions = args?.dimensions && Array.isArray(args.dimensions) && args.dimensions.length > 0;
    const hasWhereClauses = args?.where_clauses && Array.isArray(args.where_clauses) && args.where_clauses.length > 0;
    const hasDateRange = args?.date_range_start && args?.date_range_end;

    if (hasMetrics || hasDimensions || hasWhereClauses || hasDateRange) {
      // We have meaningful args, show detailed message
      chartDetailsMessage = formatChartDetails(argsText);
    } else {
      // No meaningful args yet, show simple message
      chartDetailsMessage = 'Generating chart from semantic layer';
    }
  } catch (e) {
    // If parsing fails, show simple message
    chartDetailsMessage = 'Generating chart from semantic layer';
  }

  const handleLoadChart = () => {
    setIsLoaded(true);
    if (analysisId && handlers?.onAnalysisGenerated) {
      handlers.onAnalysisGenerated(analysisId);
    }
  };

  const handleToggleExpand = () => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
    // When expanding, automatically load the chart if not already loaded
    if (newExpanded && !isLoaded && analysisId) {
      handleLoadChart();
    }
  };

  return (
    <Card>
      <div className={cn(
        "p-3",
        isRunning 
          ? "bg-primary/10 border-b border-primary/20" 
          : hasError 
            ? "bg-muted/50 border-b border-border" 
            : "bg-muted/50 border-b border-border"
      )}>
        <div className="flex items-center gap-3">
          <div className="flex-1 space-y-1">
            {isRunning ? (
              <div className="flex items-center gap-2">
                <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
                <p className="text-sm m-0 text-foreground">
                  {chartDetailsMessage}
                </p>
              </div>
            ) : hasError ? (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <BarChart3Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <p className="text-sm m-0 text-muted-foreground">
                    {chartDetailsMessage}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <XCircleIcon className="h-4 w-4 text-destructive flex-shrink-0" />
                  <p className="text-sm m-0 text-destructive">
                    Error while creating chart
                  </p>
                  {fullErrorMessage && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-destructive hover:text-destructive/80 hover:bg-destructive/10"
                      onClick={() => setShowErrorDialog(true)}
                    >
                      <Info className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <BarChart3Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <p className="text-sm m-0 text-muted-foreground">
                    {chartDetailsMessage}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2Icon className="h-4 w-4 text-success flex-shrink-0" />
                  <p className="text-sm m-0 text-foreground">
                    Created chart: {chartName}
                  </p>
                </div>
              </div>
            )}
          </div>
          {isComplete && !hasError && analysisId && (
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={handleToggleExpand}
              >
                {isExpanded ? (
                  <>
                    <ChevronUpIcon className="h-4 w-4" />
                    Hide
                  </>
                ) : (
                  <>
                    <ChevronDownIcon className="h-4 w-4" />
                    Show
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
      {isComplete && analysisId && isExpanded && (
        <div className="min-h-[200px] p-3">
          {isLoaded ? (
            <Chart analysisId={analysisId} enableExploreFromHere={true} disableSave={true} />
          ) : (
            <ChartPlaceholder analysisId={analysisId} onLoad={handleLoadChart} />
          )}
        </div>
      )}

      {/* Data Explorer Modal */}
      {analysis && analysisId && (
        <FullScreenTakeover
          show={showDataExplorer}
          onHide={() => setShowDataExplorer(false)}
          noCloseButton={true}
          title="Data Explorer"
        >
          <DataExplorer
            analysis={analysis}
            originalAnalysisId={analysisId}
            onAnalysisChange={(updatedAnalysis) => {
              // Update the local analysis state if needed
              // The analysis will be refetched by useAnalysis hook
            }}
            onClose={() => setShowDataExplorer(false)}
          />
        </FullScreenTakeover>
      )}

      {/* Error Details Dialog */}
      <Dialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Error Details</DialogTitle>
            <DialogDescription>
              Full error message from chart creation
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="mt-4">
            <pre className="text-sm bg-muted p-4 rounded-md overflow-x-auto whitespace-pre-wrap break-words">
              {fullErrorMessage || "No error details available"}
            </pre>
          </DialogBody>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// Type assertion to match assistant-ui's expected type
export const ChartToolCall = ChartToolCallComponent as React.ComponentType<any>;

